import React, { useState } from 'react';
import { useModels, evaluateGovernanceRisk } from '../contexts/ModelContext';
import { 
  PlusIcon, 
  BoltIcon, 
  ShieldCheckIcon, 
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

function OnboardForm() {
  const { addModel } = useModels();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    baseModel: 'Meta Llama 3.1 70B',
    useCase: 'Credit Scoring',
    customUseCase: '',
    jurisdiction: 'EU & US (Colorado)',
    isGPAI: false,
    trainingFlops: '',
    humanOversight: 'Human-in-the-loop',
    enableJevGuardrails: true,
    description: ''
  });

  const predefinedUseCases = [
    // 🚨 EU AI Act Article 5 - Strictly Prohibited AI Practices
    { group: 'EU AI Act Article 5 Prohibited Practices (Banned in EU)', items: [
      'Social Scoring',
      'Untargeted Facial Scraping',
      'Emotion Recognition in Workplace',
      'Emotion Recognition in Schools',
      'Sensitive Biometric Categorization',
      'Subliminal Cognitive Manipulation',
      'Individual Predictive Policing'
    ]},
    // ⚠️ Annex III High-Risk / Colorado SB 205 Consequential Decisions
    { group: 'Annex III High-Risk / Colorado Consequential Decisions', items: [
      'Credit Scoring',
      'Fraud Detection',
      'Hiring & Resume Screening',
      'Medical Diagnosis',
      'Clinical Triage',
      'Legal Decision Support',
      'Insurance Underwriting',
      'Critical Infrastructure Management',
      'Biometric Identification'
    ]},
    // 🔮 GPAI Foundation Models
    { group: 'General Purpose AI (Articles 51–56)', items: [
      'General Purpose AI (GPAI)',
      'Frontier Foundation Model',
      'Multimodal Reasoning Engine'
    ]},
    // 💬 Limited Risk (Article 50 Transparency)
    { group: 'Limited Risk (Article 50 Transparency Obligations)', items: [
      'Chatbot & Customer Support',
      'Content Moderation',
      'Document Processing',
      'Translation & Localization',
      'Sentiment Analysis',
      'Code Generation & Review',
      'Text Summarization'
    ]},
    // 🟢 Minimal Risk
    { group: 'Minimal Risk (Voluntary Codes of Conduct)', items: [
      'Recommendation',
      'Search Enhancement',
      'Spelling & Grammar Correction',
      'Language Detection'
    ]},
    { group: 'Other', items: ['Custom'] }
  ];

  const baseModels = [
    'Meta Llama 3.1 70B',
    'Meta Llama 3.1 405B (Frontier)',
    'Mistral Large 2 (123B)',
    'Alibaba Qwen 2.5 72B',
    'DeepSeek R1 (671B MoE)',
    'Claude 3.5 Sonnet',
    'Google MedGemma 27B',
    'Microsoft Phi-4 (14B)',
    'OpenAI Whisper v3',
    'Custom Fine-Tuned Model'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const activeUseCase = formData.useCase === 'Custom' ? formData.customUseCase : formData.useCase;
  const evaluation = evaluateGovernanceRisk(activeUseCase, {
    baseModel: formData.baseModel,
    isGPAI: formData.isGPAI || formData.baseModel.includes('405B'),
    trainingFlops: formData.isGPAI ? 3.8e25 : 0
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const modelPayload = {
      name: formData.name,
      baseModel: formData.baseModel,
      useCase: activeUseCase,
      jurisdiction: formData.jurisdiction,
      isGPAI: formData.isGPAI || formData.baseModel.includes('405B'),
      trainingFlops: formData.isGPAI ? '3.8e25 FLOPs' : '<10^25 FLOPs',
      humanOversight: formData.humanOversight,
      enableJevGuardrails: formData.enableJevGuardrails,
      description: formData.description
    };

    const result = await addModel(modelPayload);
    
    if (result.success) {
      alert(`Model successfully onboarded! Evaluated Risk Tier: ${evaluation.tier}. Added to governance registry.`);
      setFormData({
        name: '',
        baseModel: 'Meta Llama 3.1 70B',
        useCase: 'Credit Scoring',
        customUseCase: '',
        jurisdiction: 'EU & US (Colorado)',
        isGPAI: false,
        trainingFlops: '',
        humanOversight: 'Human-in-the-loop',
        enableJevGuardrails: true,
        description: ''
      });
      setIsOpen(false);
    } else {
      alert('Error onboarding model: ' + result.error);
    }
    
    setLoading(false);
  };

  if (!isOpen) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
              <PlusIcon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Onboard New AI Model or Agent</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Automated statutory risk classification (EU AI Act, Colorado SB 205, NIST SP 1270-1) and Jev System 1 Arbiter sealing
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-xs font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition-colors"
          >
            <PlusIcon className="-ml-1 mr-1.5 h-4 w-4" />
            Launch Onboarding Wizard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 animate-in fade-in duration-200">
      <div className="flex justify-between items-start border-b border-gray-100 pb-4 mb-6">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            Enterprise AI Model Onboarding Wizard
            <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold">
              2026 Regulatory Engine
            </span>
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Specify system parameters to automatically evaluate statutory obligations, FRIA requirements, and edge arbiter seals.
          </p>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Model Name */}
          <div>
            <label htmlFor="name" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Model / Service Name *
            </label>
            <input
              type="text"
              name="name"
              id="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              className="block w-full border border-gray-300 rounded-lg shadow-xs py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs text-gray-900"
              placeholder="e.g., CreditRisk-Arbiter-v2"
            />
          </div>

          {/* Base Model Architecture */}
          <div>
            <label htmlFor="baseModel" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Base Architecture / Foundation Model *
            </label>
            <select
              name="baseModel"
              id="baseModel"
              required
              value={formData.baseModel}
              onChange={handleInputChange}
              className="block w-full border border-gray-300 rounded-lg shadow-xs py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs text-gray-900 bg-white"
            >
              {baseModels.map((bm) => (
                <option key={bm} value={bm}>{bm}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Use Case */}
          <div>
            <label htmlFor="useCase" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Intended Use Case *
            </label>
            <select
              name="useCase"
              id="useCase"
              required
              value={formData.useCase}
              onChange={handleInputChange}
              className="block w-full border border-gray-300 rounded-lg shadow-xs py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs text-gray-900 bg-white"
            >
              {predefinedUseCases.map((grp) => (
                <optgroup key={grp.group} label={grp.group}>
                  {grp.items.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Target Jurisdiction */}
          <div>
            <label htmlFor="jurisdiction" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Deployment Jurisdiction *
            </label>
            <select
              name="jurisdiction"
              id="jurisdiction"
              required
              value={formData.jurisdiction}
              onChange={handleInputChange}
              className="block w-full border border-gray-300 rounded-lg shadow-xs py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs text-gray-900 bg-white"
            >
              <option value="EU & US (Colorado)">EU (Act 2024/1689) & US (Colorado SB 205)</option>
              <option value="EU Only">European Union Only (EU AI Act 2024/1689)</option>
              <option value="US - Colorado">US - Colorado (SB 24-205 Consequential)</option>
              <option value="US - Federal">US - Federal (NIST AI RMF 1.0)</option>
              <option value="Global">Global Deployment</option>
            </select>
          </div>
        </div>

        {formData.useCase === 'Custom' && (
          <div>
            <label htmlFor="customUseCase" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Custom Use Case Specification *
            </label>
            <input
              type="text"
              name="customUseCase"
              id="customUseCase"
              required
              value={formData.customUseCase}
              onChange={handleInputChange}
              className="block w-full border border-gray-300 rounded-lg shadow-xs py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs text-gray-900"
              placeholder="Describe purpose, domain, and affected subjects"
            />
          </div>
        )}

        {/* Human Oversight & Jev System 1 Guardrails */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-xl border border-slate-200">
          <div>
            <label htmlFor="humanOversight" className="block text-xs font-semibold text-slate-800 uppercase tracking-wider mb-1">
              Human Oversight Mechanism (EU Art. 14)
            </label>
            <select
              name="humanOversight"
              id="humanOversight"
              value={formData.humanOversight}
              onChange={handleInputChange}
              className="block w-full border border-gray-300 rounded-lg shadow-xs py-2 px-3 text-xs bg-white text-gray-900"
            >
              <option value="Human-in-the-loop">Human-in-the-loop (Mandatory human approval before action)</option>
              <option value="Human-on-the-loop">Human-on-the-loop (Real-time monitoring with intervention)</option>
              <option value="Human-in-command">Human-in-command (Supervisory kill-switch & override)</option>
            </select>
          </div>

          <div className="space-y-3 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="isGPAI"
                checked={formData.isGPAI || formData.baseModel.includes('405B')}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
              />
              <span className="text-xs font-semibold text-gray-800">
                Frontier Compute Threshold (&ge; 10²⁵ FLOPs GPAI Systemic Risk)
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="enableJevGuardrails"
                checked={formData.enableJevGuardrails}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4"
              />
              <span className="text-xs font-semibold text-emerald-900 flex items-center gap-1">
                <BoltIcon className="w-3.5 h-3.5 text-emerald-600" />
                Enable Jev System 1 Reflex Guardrails & Zero-Knowledge PII Vault
              </span>
            </label>
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
            Technical & Governance Description
          </label>
          <textarea
            name="description"
            id="description"
            rows={2}
            value={formData.description}
            onChange={handleInputChange}
            className="block w-full border border-gray-300 rounded-lg shadow-xs py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs text-gray-900"
            placeholder="Document data provenance, training methodology, and intended deployment environment"
          />
        </div>

        {/* Real-time Dynamic Regulatory Risk Assessment Preview Card */}
        <div className={`p-4 rounded-xl border ${
          evaluation.tier === 'Prohibited' 
            ? 'bg-red-50 border-red-300 text-red-900' 
            : evaluation.tier === 'High-Risk' 
            ? 'bg-rose-50 border-rose-200 text-rose-950'
            : evaluation.tier === 'GPAI Systemic'
            ? 'bg-purple-50 border-purple-200 text-purple-950'
            : 'bg-emerald-50 border-emerald-200 text-emerald-950'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
              {evaluation.tier === 'Prohibited' ? (
                <ExclamationTriangleIcon className="w-4 h-4 text-red-700" />
              ) : (
                <ShieldCheckIcon className="w-4 h-4 text-emerald-700" />
              )}
              Regulatory Classifier Evaluation Preview
            </span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-extrabold ${
              evaluation.tier === 'Prohibited' ? 'bg-red-700 text-white' :
              evaluation.tier === 'High-Risk' ? 'bg-rose-200 text-rose-900' :
              evaluation.tier === 'GPAI Systemic' ? 'bg-purple-200 text-purple-900' :
              'bg-emerald-200 text-emerald-900'
            }`}>
              {evaluation.tier}
            </span>
          </div>

          <p className="text-xs font-semibold mb-1">
            Citation: {evaluation.regulation}
          </p>
          <p className="text-xs text-gray-700 mb-2">
            {evaluation.description}
          </p>

          <div className="pt-2 border-t border-gray-200/50 flex flex-wrap gap-4 text-[11px]">
            <span>
              FRIA (Art. 27): <strong>{evaluation.requiresFRIA ? 'Mandatory' : 'Exempt'}</strong>
            </span>
            <span>
              Colorado Algorithmic Audit: <strong>{evaluation.requiresColoradoAudit ? 'Required' : 'Exempt'}</strong>
            </span>
            <span>
              Penalty Exposure: <strong>{evaluation.penalties}</strong>
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end items-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-5 py-2 rounded-lg text-xs font-bold text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
              evaluation.tier === 'Prohibited'
                ? 'bg-red-700 hover:bg-red-800'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? 'Evaluating...' : evaluation.tier === 'Prohibited' ? 'Register as Prohibited Violation' : 'Register AI System'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default OnboardForm;